import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { palette } from "../../../../../assets/styles/Palette";
import { useAtom } from "jotai";
import {
  IDEA_GENERATION_START_POSITION,
  IDEA_GENERATION_SELECTED_START_POSITION,
} from "../../../../AtomStates";

const MAX_SELECTIONS = 8;

const MoleculeCitizenTagList = ({ items, disabled }) => {
  const [ideaGenerationStartPosition, setIdeaGenerationStartPosition] =
    useAtom(IDEA_GENERATION_START_POSITION);
  const [
    ideaGenerationSelectedStartPosition,
    setIdeaGenerationSelectedStartPosition,
  ] = useAtom(IDEA_GENERATION_SELECTED_START_POSITION);

  const [selectedIndexes, setSelectedIndexes] = useState([]);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [addedCount, setAddedCount] = useState(0);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editTheme, setEditTheme] = useState("");
  const [editSituation, setEditSituation] = useState("");
  const [editCoreNeed, setEditCoreNeed] = useState("");

  useEffect(() => {
    if (items && ideaGenerationSelectedStartPosition) {
      const indexes = items
        .map((item, index) =>
          ideaGenerationSelectedStartPosition.some(
            (selected) => selected.main_theme === item.main_theme
          )
            ? index
            : -1
        )
        .filter((index) => index !== -1);
      setSelectedIndexes(indexes);
    }
  }, [items, ideaGenerationSelectedStartPosition]);

  const handleToggleSelect = (index) => {
    if (disabled) return;

    let newSelected;
    if (selectedIndexes.includes(index)) {
      newSelected = selectedIndexes.filter((i) => i !== index);
    } else {
      if (selectedIndexes.length >= MAX_SELECTIONS) return;
      newSelected = [...selectedIndexes, index];
    }
    setSelectedIndexes(newSelected);
    setIdeaGenerationSelectedStartPosition(
      newSelected.map((i) => items[i])
    );
  };

  const handleToggleExpand = (index) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  const parseRawData = (rawData) => {
    if (!rawData) return { situation: "", coreNeed: "" };
    const parts = rawData.split(" - ");
    return {
      situation: parts[0] || "",
      coreNeed: parts.slice(1).join(" - ") || "",
    };
  };

  const handleStartAddTheme = () => {
    const newTag = {
      id: Date.now(),
      main_theme: "",
      raw_data: " - ",
      _isNew: true,
    };

    setIdeaGenerationStartPosition((prev) => {
      const prevArray = Array.isArray(prev) ? prev : [];
      return [...prevArray, newTag];
    });

    const newIndex = (Array.isArray(items) ? items.length : 0);
    setEditingIndex(newIndex);
    setExpandedIndex(newIndex);
    setEditTheme("");
    setEditSituation("");
    setEditCoreNeed("");
  };

  const handleConfirmAdd = (index) => {
    if (!editTheme.trim()) {
      setIdeaGenerationStartPosition((prev) => {
        const prevArray = Array.isArray(prev) ? prev : [];
        return prevArray.filter((_, i) => i !== index);
      });
      setEditingIndex(null);
      return;
    }

    setIdeaGenerationStartPosition((prev) => {
      const prevArray = Array.isArray(prev) ? prev : [];
      return prevArray.map((item, i) =>
        i === index
          ? {
              ...item,
              main_theme: editTheme.trim(),
              raw_data: `${editSituation.trim()} - ${editCoreNeed.trim()}`,
              _isNew: undefined,
            }
          : item
      );
    });

    setAddedCount((prev) => prev + 1);
    setEditingIndex(null);
  };

  const handleCancelAdd = (index) => {
    setIdeaGenerationStartPosition((prev) => {
      const prevArray = Array.isArray(prev) ? prev : [];
      return prevArray.filter((_, i) => i !== index);
    });
    setEditingIndex(null);
  };

  return (
    <>
      <ListContainer>
        {Array.isArray(items) &&
          items.filter(Boolean).map((item, index) => {
            const isEditing = editingIndex === index;
            const isSelected = selectedIndexes.includes(index);
            const isExpanded = expandedIndex === index;
            const { situation, coreNeed } = parseRawData(item.raw_data);
            const isDisabledUnselected =
              !isSelected && selectedIndexes.length >= MAX_SELECTIONS;

            if (isEditing) {
              return (
                <ThemeCard key={item.id || index} selected={false} disabled={false}>
                  <ThemeHeader>
                    <ThemeIndex selected={false}>
                      {String(index + 1).padStart(2, "0")}
                    </ThemeIndex>
                    <EditInputWrap>
                      <EditInput
                        autoFocus
                        placeholder="테마명을 입력하세요"
                        value={editTheme}
                        onChange={(e) => setEditTheme(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleConfirmAdd(index);
                        }}
                      />
                    </EditInputWrap>
                    <ExpandButton
                      onClick={() =>
                        setExpandedIndex(isExpanded ? null : index)
                      }
                      isExpanded={isExpanded}
                    >
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M5 8L10 13L15 8" stroke={palette.gray500} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </ExpandButton>
                  </ThemeHeader>

                  {isExpanded && (
                    <ThemeDetail>
                      <DetailRow>
                        <DetailLabel>상황</DetailLabel>
                        <EditInput
                          placeholder="상황을 입력하세요 (선택)"
                          value={editSituation}
                          onChange={(e) => setEditSituation(e.target.value)}
                        />
                      </DetailRow>
                      <DetailRow>
                        <DetailLabel>핵심 니즈</DetailLabel>
                        <EditInput
                          placeholder="핵심 니즈를 입력하세요 (선택)"
                          value={editCoreNeed}
                          onChange={(e) => setEditCoreNeed(e.target.value)}
                        />
                      </DetailRow>
                      <EditButtonRow>
                        <CancelButton onClick={() => handleCancelAdd(index)}>
                          취소
                        </CancelButton>
                        <ConfirmButton
                          onClick={() => handleConfirmAdd(index)}
                          disabled={!editTheme.trim()}
                        >
                          완료
                        </ConfirmButton>
                      </EditButtonRow>
                    </ThemeDetail>
                  )}
                </ThemeCard>
              );
            }

            return (
              <ThemeCard
                key={item.id || index}
                selected={isSelected}
                disabled={isDisabledUnselected}
              >
                <ThemeHeader>
                  <CheckboxArea
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!disabled) handleToggleSelect(index);
                    }}
                    disabled={disabled || isDisabledUnselected}
                  >
                    <Checkbox selected={isSelected} disabled={disabled || isDisabledUnselected}>
                      {isSelected && (
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 12 12"
                          fill="none"
                        >
                          <path
                            d="M2 6L5 9L10 3"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </Checkbox>
                  </CheckboxArea>

                  <ThemeContent
                    onClick={() => handleToggleExpand(index)}
                  >
                    <ThemeIndex selected={isSelected}>
                      {String(index + 1).padStart(2, "0")}
                    </ThemeIndex>
                    <ThemeTitle selected={isSelected}>
                      {item.main_theme || ""}
                    </ThemeTitle>
                  </ThemeContent>

                  <ExpandButton
                    onClick={() => handleToggleExpand(index)}
                    isExpanded={isExpanded}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        d="M5 8L10 13L15 8"
                        stroke={palette.gray500}
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </ExpandButton>
                </ThemeHeader>

                {isExpanded && (situation || coreNeed) && (
                  <ThemeDetail>
                    {situation && (
                      <DetailRow>
                        <DetailLabel>상황</DetailLabel>
                        <DetailValue>{situation}</DetailValue>
                      </DetailRow>
                    )}
                    {coreNeed && (
                      <DetailRow>
                        <DetailLabel>핵심 니즈</DetailLabel>
                        <DetailValue>{coreNeed}</DetailValue>
                      </DetailRow>
                    )}
                  </ThemeDetail>
                )}
              </ThemeCard>
            );
          })}

        {!disabled && addedCount < 3 && editingIndex === null && (
          <AddThemeButton onClick={handleStartAddTheme}>
            <PlusIcon>+</PlusIcon>
            <span>새로운 테마 추가하기</span>
          </AddThemeButton>
        )}
      </ListContainer>

      <SelectionCount>
        <CountText
          complete={selectedIndexes.length === MAX_SELECTIONS}
        >
          {selectedIndexes.length} / {MAX_SELECTIONS} 선택됨
        </CountText>
        {selectedIndexes.length !== MAX_SELECTIONS && (
          <CountHint>정확히 8개를 선택해야 다음 단계로 진행할 수 있습니다</CountHint>
        )}
      </SelectionCount>
    </>
  );
};

export default MoleculeCitizenTagList;

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const ThemeCard = styled.div`
  border: 1.5px solid
    ${(props) =>
      props.selected ? palette.primary : palette.outlineGray};
  border-radius: 12px;
  background: ${(props) =>
    props.selected ? palette.primaryLightest : palette.white};
  transition: all 0.2s ease;
  overflow: hidden;
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};

  &:hover {
    border-color: ${(props) =>
      props.disabled
        ? palette.outlineGray
        : props.selected
        ? palette.primary
        : palette.gray400};
  }
`;

const ThemeHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 14px 16px;
  gap: 12px;
  cursor: pointer;
`;

const CheckboxArea = styled.div`
  display: flex;
  align-items: center;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  flex-shrink: 0;
`;

const Checkbox = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 6px;
  border: 2px solid
    ${(props) =>
      props.selected ? palette.primary : palette.gray300};
  background: ${(props) =>
    props.selected ? palette.primary : palette.white};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
`;

const ThemeContent = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
`;

const ThemeIndex = styled.span`
  font-family: "Poppins", sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${(props) =>
    props.selected ? palette.primary : palette.gray400};
  flex-shrink: 0;
`;

const ThemeTitle = styled.span`
  font-family: "Pretendard", sans-serif;
  font-size: 15px;
  font-weight: ${(props) => (props.selected ? "600" : "400")};
  color: ${(props) =>
    props.selected ? palette.gray800 : palette.gray600};
  line-height: 1.5;
  word-break: keep-all;
`;

const ExpandButton = styled.button`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  transform: ${(props) =>
    props.isExpanded ? "rotate(180deg)" : "rotate(0deg)"};
  transition: transform 0.2s ease;
`;

const ThemeDetail = styled.div`
  padding: 0 16px 14px 50px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  border-top: 1px solid ${palette.outlineGray};
  padding-top: 12px;
  margin: 0 16px;
`;

const DetailRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
`;

const DetailLabel = styled.span`
  font-family: "Pretendard", sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${palette.primary};
  flex-shrink: 0;
  min-width: 60px;
`;

const DetailValue = styled.span`
  font-family: "Pretendard", sans-serif;
  font-size: 13px;
  font-weight: 400;
  color: ${palette.gray700};
  line-height: 1.6;
`;

const AddThemeButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  height: 48px;
  background: ${palette.white};
  border: 1.5px dashed ${palette.gray300};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  span {
    font-family: "Pretendard", sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: ${palette.gray500};
  }

  &:hover {
    border-color: ${palette.primary};
    background: ${palette.primaryLightest};

    span {
      color: ${palette.primary};
    }
  }
`;

const PlusIcon = styled.div`
  font-size: 18px;
  color: ${palette.gray500};
  line-height: 1;
`;

const SelectionCount = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
`;

const CountText = styled.span`
  font-family: "Pretendard", sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: ${(props) =>
    props.complete ? palette.primary : palette.gray500};
`;

const CountHint = styled.span`
  font-family: "Pretendard", sans-serif;
  font-size: 13px;
  color: ${palette.gray400};
`;

const EditInputWrap = styled.div`
  flex: 1;
  min-width: 0;
`;

const EditInput = styled.input`
  width: 100%;
  height: 36px;
  padding: 0 12px;
  border: 1px solid ${palette.outlineGray};
  border-radius: 8px;
  font-size: 14px;
  font-family: "Pretendard", sans-serif;
  color: ${palette.gray800};
  box-sizing: border-box;

  &::placeholder {
    color: ${palette.gray300};
  }

  &:focus {
    outline: none;
    border-color: ${palette.primary};
  }
`;

const EditButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 4px;
`;

const CancelButton = styled.button`
  height: 32px;
  padding: 0 16px;
  background: ${palette.white};
  color: ${palette.gray600};
  border: 1px solid ${palette.outlineGray};
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background: ${palette.gray50};
  }
`;

const ConfirmButton = styled.button`
  height: 32px;
  padding: 0 16px;
  background: ${(props) =>
    props.disabled ? palette.gray200 : palette.primary};
  color: ${palette.white};
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};

  &:hover {
    background: ${(props) =>
      props.disabled ? palette.gray200 : "#1a5fd9"};
  }
`;
